import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axiosInstance from "../../../../Utils/axiosInstance";
import { businessPlanningTableMap, normalizeSegmentKey, formatSegmentTitle } from "./utils";

const DEFAULT_YEAR = 2026;

export const useEDASegment = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    dashboard,
    selectedProcess,
    segment,
    month: initialMonth,
    year: initialYear,
  } = location.state || {};

  const segmentKey = normalizeSegmentKey(segment);
  const segmentTitle = formatSegmentTitle(segment);
  const [periodOptions, setPeriodOptions] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [selectedYear, setSelectedYear] = useState(initialYear || DEFAULT_YEAR);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [classificationData, setClassificationData] = useState([]);
  const [classificationLoading, setClassificationLoading] = useState(false);
  const [classificationError, setClassificationError] = useState("");

  const monthColumns = useMemo(() => {
    if (!rows.length) return [];
    const row = rows[0];
    return Object.keys(row)
      .filter((key) => key.endsWith("_Actual"))
      .map((key) => {
        const prefix = key.replace("_Actual", "");
        return {
          key,
          title: row[`${prefix}_MonthYear`] || prefix,
        };
      });
  }, [rows]);

  const transformedClassificationData = useMemo(() => {
    if (!classificationData.length) return [];
    const map = {};
    classificationData.forEach((monthObj) => {
      const month = monthObj.MonthYear;
      monthObj.Items.forEach((item) => {
        const key = item.Particulars;
        if (!map[key]) {
          map[key] = { Particulars: key };
        }
        Object.entries(item).forEach(([field, value]) => {
          if (field !== "Particulars" && field !== "Category as per BP") {
            map[key][`${month}_${field}`] = value;
          }
        });
      });
    });
    return Object.values(map);
  }, [classificationData]);

  const classificationMonths = useMemo(
    () => classificationData.map((x) => x.MonthYear),
    [classificationData]
  );

  const segmentColumns = useMemo(() => {
    if (!classificationData.length) return [];
    const firstItem = classificationData[0]?.Items?.[0];
    if (!firstItem) return [];
    return Object.keys(firstItem).filter(
      (key) => key !== "Particulars" && key !== "Category as per BP"
    );
  }, [classificationData]);

  const handleRowClick = (category) => {
    if (selectedCategory === category) {
      setSelectedCategory(null);
    } else {
      setSelectedCategory(category);
    }
  };

  useEffect(() => {
    let ignore = false;
    const fetchPeriodOptions = async () => {
      try {
        const response = await axiosInstance.get("/eda/fixed-cost-periods");
        if (!ignore && response?.data?.status) {
          const options = response.data.items.map((item) => ({
            value: item.value,
            label: item.label,
            months: item.value.split(",").map(Number),
          }));
          setPeriodOptions(options);
          if (options.length > 0) {
            setSelectedPeriod(options[0]);
          }
        }
      } catch (error) {
        console.error("Failed to fetch period options", error);
      }
    };
    fetchPeriodOptions();
    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    if (!location.state || !selectedPeriod) {
      return;
    }
    let ignore = false;
    const fetchBusinessPlanningSummary = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await axiosInstance.post("/eda/business-planning-summary", {
          Months: selectedPeriod?.months?.join(","),
          Year: selectedYear,
          TableName: businessPlanningTableMap[segmentKey],
        });
        if (!ignore && response?.data?.status) {
          setRows(response?.data?.items || []);
        }
      } catch (apiError) {
        if (!ignore) {
          setError(
            apiError?.response?.data?.message || "Failed to fetch business planning summary."
          );
          setRows([]);
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };
    fetchBusinessPlanningSummary();
    return () => {
      ignore = true;
    };
  }, [location.state, segmentKey, selectedPeriod, selectedYear]);

  useEffect(() => {
    if (!selectedCategory || !location.state) {
      setClassificationData([]);
      setClassificationError("");
      return;
    }
    let ignore = false;
    const fetchClassificationData = async () => {
      setClassificationLoading(true);
      setClassificationError("");
      setClassificationData([]);
      try {
        const response = await axiosInstance.post("/eda/fixed-cost-classification", {
          Months: selectedPeriod?.months?.join(","),
          Year: selectedYear,
          Category: selectedCategory,
          Type: segmentKey.toUpperCase(),
        });
        if (!ignore && response?.data?.status) {
          setClassificationData(response.data.items || []);
        } else if (!ignore) {
          setClassificationError(response?.data?.message || "Failed to fetch classification data.");
        }
      } catch (apiError) {
        if (!ignore) {
          setClassificationError(
            apiError?.response?.data?.message || "Failed to fetch classification data."
          );
        }
      } finally {
        if (!ignore) {
          setClassificationLoading(false);
        }
      }
    };
    fetchClassificationData();
    return () => {
      ignore = true;
    };
  }, [selectedCategory, selectedPeriod, selectedYear, location.state, segmentKey]);

  return {
    navigate,
    location,
    dashboard,
    selectedProcess,
    segmentTitle,
    periodOptions,
    selectedPeriod,
    setSelectedPeriod,
    selectedYear,
    setSelectedYear,
    rows,
    loading,
    error,
    selectedCategory,
    classificationLoading,
    classificationError,
    classificationData,
    monthColumns,
    transformedClassificationData,
    classificationMonths,
    segmentColumns,
    handleRowClick,
  };
};
